import { PrebidConfig } from './../../shared/bb-prebid-fields/bb-prebid-fields.component';
import { PrebidService } from 'app/services/prebid.service';
import { TranslateService } from '@ngx-translate/core';
import { ToCodePipe } from './../../../pipes/toCode.pipe';
import { Auction } from './../../../interfaces/auction';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { QueryParameter } from '../../../interfaces/queryString';
import { SapiService, SapiQueryResponse } from '../../../services/sapi.service';
import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';

@Component({
	selector: 'auction-details-general',
	templateUrl: './auction-details-general.component.html',
	styleUrls: ['./auction-details-general.component.scss']
})

export class AuctionDetailsGeneralComponent implements OnInit, OnChanges, OnDestroy {
	@Input() auction: Auction;
	@Input() form: NgForm;

	private nameValueUpdated: Subject<string> = new Subject<string>();
	apiFrameworkOptions = [
		{ value: 1, label: 'VPAID 1.0' },
		{ value: 2, label: 'VPAID 2.0' },
		{ value: 3, label: 'MRAID-1' },
		{ value: 4, label: 'ORMMA' },
		{ value: 5, label: 'MRAID-2' },
		{ value: 6, label: 'MRAID-3' },
	];
	bidResponseProtocolOptions = [
		{ value: 1, label: 'VAST 1.0' },
		{ value: 2, label: 'VAST 2.0' },
		{ value: 3, label: 'VAST 3.0' },
		{ value: 4, label: 'VAST 1.0 Wrapper' },
		{ value: 5, label: 'VAST 2.0 Wrapper' },
		{ value: 6, label: 'VAST 3.0 Wrapper' },
		{ value: 7, label: 'VAST 4.0' },
		{ value: 8, label: 'VAST 4.0 Wrapper' },
		{ value: 9, label: 'DAAST 1.0' },
		{ value: 10, label: ' DAAST 1.0 Wrapper' },
	];

	playbackMethodOptions = [
		{value: 1, label: 'Initiates on Page Load with Sound On'},
		{value: 2, label: 'Initiates on Page Load with Sound Off by Default'},
		{value: 3, label: 'Initiates on Click with Sound On'},
		{value: 4, label: 'Initiates on Mouse-Over with Sound On'},
		{value: 5, label: 'Initiates on Entering Viewport with Sound On'},
		{value: 6, label: 'Initiates on Entering Viewport with Sound Off by Default'},
	];

	mimeTypeOptions = [
		{ value: 'application/javascript', label: 'application/javascript' },
		{ value: 'application/x-shockwave-flash', label: 'application/x-shockwave-flash' },
		{ value: 'video/mp4', label: 'video/mp4' },
		{ value: 'video/webm', label: 'video/webm' },
		{ value: 'video/ogg', label: 'video/ogg' },
		{ value: 'video/x-flv', label: 'video/x-flv' },
		{ value: 'video/3gpp', label: 'video/3gpp' },
		{ value: 'video/3gpp2', label: 'video/3gpp2' },
		{ value: 'video/quicktime', label: 'video/quicktime' },
		{ value: 'video/x-ms-wmv', label: 'video/x-ms-wmv' },
		{ value: 'video/x-msvideo', label: 'video/x-msvideo' },
		{ value: 'video/x-matroska', label: 'video/x-matroska' },
		{ value: 'video/x-m4v', label: 'video/x-m4v' },
		{ value: 'video/x-mpeg', label: 'video/x-mpeg' },
		{ value: 'video/x-mpeg2', label: 'video/x-mpeg2' },
		{ value: 'video/x-mpegurl', label: 'video/x-mpegurl' },
		{ value: 'video/x-ms-asf', label: 'video/x-ms-asf' },
		{ value: 'video/x-ms-asf-plugin', label: 'video/x-ms-asf-plugin' },
		{ value: 'video/x-ms-wvx', label: 'video/x-ms-wvx' },
		{ value: 'video/x-ms-wmx', label: 'video/x-ms-wmx' },
		{ value: 'video/x-ms-wm', label: 'video/x-ms-wm' },
		{ value: 'video/x-ms-wmvhd', label: 'video/x-ms-wmvhd' },
		{ value: 'video/x-ms-wmz', label: 'video/x-ms-wmz' },
		{ value: 'video/x-ms-wmd', label: 'video/x-ms-wmd' },
		{ value: 'video/x-ms-wpl', label: 'video/x-ms-wpl' },
		{ value: 'video/x-ms-wmxml', label: 'video/x-ms-wmxml' },
	];

	adunitPositionOptions = [
		{ label: this.translate.instant('UNKNOWN'), value: 0 },
		{ label: this.translate.instant('ABOVE_THE_FOLD'), value: 1 },
		// { label: this.translate.instant('2 DEPRECATED - May or may not be initially visible depending on screen size/resolution.'), value: 2 },
		{ label: this.translate.instant('BELOW_THE_FOLD'), value: 3 },
		{ label: this.translate.instant('HEADER'), value: 4 },
		{ label: this.translate.instant('FOOTER'), value: 5 },
		{ label: this.translate.instant('SIDEBAR'), value: 6 },
		{ label: this.translate.instant('FULLSCREEN'), value: 7 }
	];

	placementOptions = [
		{ value: 1, label: this.translate.instant('IN_STREAM')},
		{ value: 2, label: this.translate.instant('IN_BANNER')},
		{ value: 3, label: this.translate.instant('IN_ARTICLE')},
		{ value: 4, label: this.translate.instant('IN_FEED')},
		{ value: 5, label: this.translate.instant('INTERSTITIAL') + '/' + this.translate.instant('FLOATING')},
	];

	deliveryOptions = [
		{ value: 1, label: this.translate.instant('STREAMING')},
		{ value: 2, label: this.translate.instant('PROGRESSIVE')},
		{ value: 3, label: this.translate.instant('DOWNLOAD')},
	];

	fieldPaths = [
		'/mediaTypes/video/minduration',
		'/mediaTypes/video/maxduration',
		'/mediaTypes/video/minbitrate',
		'/mediaTypes/video/maxbitrate',
		'/mediaTypes/video/mimes',
		'/mediaTypes/video/w',
		'/mediaTypes/video/h',
		'/mediaTypes/video/pos',
		'/mediaTypes/video/placement',
		'/mediaTypes/video/playbackmethod',
		'/mediaTypes/video/playerSize',
		'/mediaTypes/video/api',
		'/mediaTypes/video/protocols',
		'/mediaTypes/video/delivery',
		'/firstPartyData/skipEnrichments',
		'/gptPreAuction/enabled',
		'/bidderTimeout',
		'/deviceAccess'
	];

	pathOptionsRef = {
		'/mediaTypes/video/api': this.apiFrameworkOptions,
		'/mediaTypes/video/placement': this.placementOptions,
		'/mediaTypes/video/protocols': this.bidResponseProtocolOptions,
		'/mediaTypes/video/playbackmethod': this.playbackMethodOptions,
		'/mediaTypes/video/delivery': this.deliveryOptions,
		'/mediaTypes/video/pos': this.adunitPositionOptions
	};
	defaultValues = {};
	playerSizeWidth: number;
	playerSizeHeight: number;
	subscriptions = new Subscription();

	constructor(
		private sapiService: SapiService,
		private toCodePipe: ToCodePipe,
		private prebidService: PrebidService,
		private translate: TranslateService
	) {
		this.nameValueUpdated
			.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe(value => this.updateCode(value));
	}

	ngOnInit() {
		this.subscriptions.add(this.prebidService.getPrebidConfig().subscribe((prebidData: PrebidConfig) => {
			this.prebidService.handlePrebidData(prebidData, this.prebidService.publicationPrebidParams);
			this.fieldPaths.forEach(path => {
				const defaultValue = this.prebidService.getDefaultValue('__config__', path);
				if (this.pathOptionsRef[path]) {
					if (defaultValue?.map) {
						this.defaultValues[path] = defaultValue.map(v => this.pathOptionsRef[path].find(o => o.value === v)?.label).join(', ');
					} else if (typeof defaultValue === 'number') {
						this.defaultValues[path] = this.pathOptionsRef[path].find(o => o.value === defaultValue)?.label;
					}
				} else {
					this.defaultValues[path] = defaultValue;
				}
			});
		}));
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.auction && this.auction) {
			if (!this.auction?.prebidConfiguration) {
				this.auction.prebidConfiguration = {};
			}
			if (!this.auction?.prebidConfiguration?.__config__ || JSON.stringify(this.auction?.prebidConfiguration?.__config__) === '[]') {
				this.auction.prebidConfiguration.__config__ = {};
			}
			this.setPlayerSize();
		}
	}

	trackByIndex(index: number, item: any) {
		return item?.id || index;
	}

	// for new auctions: auto create code based on name
	onNameUpdated(name: string) {
		if (name && !this.auction.id) {
			this.nameValueUpdated.next(name);
		}
	}

	updateCode(value: string) {
		if (this.auction.id) {
			return;
		}
		this.auction.code = this.toCodePipe.transform(value);

		const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.auction.code] }];

		this.sapiService.query('auction', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
			if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
				this.form.form.controls.code.setErrors({ 'not-unique': true });
				this.form.form.controls.code.markAsTouched();
				this.form.form.controls.code.markAsDirty();
			}
		});
	}

	setPlayerSize() {
		if (this.auction?.prebidConfiguration?.__config__?.['/mediaTypes/video/playerSize']?.length) {
			this.playerSizeWidth = this.auction.prebidConfiguration?.__config__['/mediaTypes/video/playerSize'][0];
			this.playerSizeHeight = this.auction.prebidConfiguration?.__config__['/mediaTypes/video/playerSize'][1];
		}
	}

	onPlayerSizeChange() {
		this.auction.prebidConfiguration.__config__['/mediaTypes/video/playerSize'] = this.playerSizeWidth && this.playerSizeHeight ? [this.playerSizeWidth, this.playerSizeHeight] : null;
	}
}
